import type {
  CreateEventRequest,
  EventCheckinCodeResponse,
  EventLiveRoomEndResponse,
  EventLiveRoomStatus,
  LiveRoomAnnouncementResponse,
  UpdateEventRequest,
  WindowedResponse,
} from '@pulsify/shared/dto'
import { createEventRequestSchema, updateEventRequestSchema } from '@pulsify/shared/dto'
import { audiencePayload } from '~/entities/event/lib/event-audience'
import { hostPayload } from '~/entities/event/lib/event-host'
import { createEventPlace, eventLocationRequest } from '~/entities/event/lib/event-location'
import * as m from '~/paraglide/messages'
import { assertValidPayload, type FieldMessage } from '~/shared/lib/assert-valid-payload'
import { api, unwrap } from '~/shared/lib/rpc'
import type { CreateEventInput, Event, ListingLocation, UpdateEventInput } from '~/shared/types'
import { occurrenceFormsToRequests } from '../lib/event-occurrences'

interface EventListParams {
  startIndex: number
  pageSize: number
  when: 'upcoming' | 'past'
  hostProfileId?: string
  q?: string
  from?: string
  to?: string
}

interface CreateEventOptions {
  listingLocation?: ListingLocation | null
}

const EN_LOCALE = { locale: 'en' } as const

const EVENT_FIELD_MESSAGES: Record<string, FieldMessage> = {
  title: () => m.eventTitleRequired(undefined, EN_LOCALE),
  location: () => m.eventLocationOrListingRequired(undefined, EN_LOCALE),
  'location.street': () => m.streetRequired(undefined, EN_LOCALE),
  'location.city': () => m.cityRequired(undefined, EN_LOCALE),
  'location.country': () => m.countryRequired(undefined, EN_LOCALE),
  startTime: () => m.startDateTimeRequired(undefined, EN_LOCALE),
  endTime: (issue) =>
    issue.code === 'custom'
      ? m.endTimeMustBeAfterStartTime(undefined, EN_LOCALE)
      : m.endDateTimeRequired(undefined, EN_LOCALE),
  'tags.mainCategoryId': () => m.categoryRequired(undefined, EN_LOCALE),
  'tags.subCategoryTags': () => m.pleaseSelectAtLeastOneSubcategory(undefined, EN_LOCALE),
}

export const eventsApi = {
  async list(businessId: string, params: EventListParams): Promise<WindowedResponse<Event>> {
    const res = await api.api.businesses[':businessId'].events.$get({
      param: { businessId },
      query: {
        startIndex: String(params.startIndex),
        pageSize: String(params.pageSize),
        when: params.when,
        ...(params.hostProfileId ? { hostProfileId: params.hostProfileId } : {}),
        ...(params.q ? { q: params.q } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
      },
    })
    return unwrap<WindowedResponse<Event>>(res)
  },

  async getById(businessId: string, eventId: string): Promise<Event> {
    const res = await api.api.businesses[':businessId'].events[':id'].$get({
      param: { businessId, id: eventId },
    })
    return unwrap<Event>(res)
  },

  async create(
    businessId: string,
    data: CreateEventInput,
    options?: CreateEventOptions,
  ): Promise<{ id: string }> {
    const occurrences =
      data.occurrences && data.occurrences.length > 0
        ? occurrenceFormsToRequests(data.occurrences)
        : null

    const payload: CreateEventRequest = {
      title: data.title,
      description: data.description,
      ...(data.descriptionLanguage ? { descriptionLanguage: data.descriptionLanguage } : {}),
      ...(data.descriptionTranslations
        ? { descriptionTranslations: data.descriptionTranslations }
        : {}),
      // For an occurrence-based event the backend derives the real envelope from
      // the occurrences; the top-level range only needs to be a valid placeholder.
      startTime: occurrences
        ? occurrences[0].startTime
        : data.startTime
          ? new Date(data.startTime).toISOString()
          : '',
      endTime: occurrences
        ? occurrences[0].endTime
        : data.endTime
          ? new Date(data.endTime).toISOString()
          : '',
      price: {
        kind: data.price.kind,
        priceFixed: 'priceFixed' in data.price ? data.price.priceFixed : undefined,
        priceRangeMin: 'priceRangeMin' in data.price ? data.price.priceRangeMin : undefined,
        priceRangeMax: 'priceRangeMax' in data.price ? data.price.priceRangeMax : undefined,
      },
      ticketUrl: data.ticketUrl || null,
      ...(data.addressHidden ? { addressHidden: true } : {}),
      ...(data.ticketUrl && data.ticketUrlKind ? { ticketUrlKind: data.ticketUrlKind } : {}),
      capacity: data.capacity || null,
      ...(data.rsvpEnabled ? { rsvpEnabled: true } : {}),
      ...(data.liveRoomMode && data.liveRoomMode !== 'open'
        ? { liveRoomMode: data.liveRoomMode }
        : {}),
      ...(data.roomVisibility ? { roomVisibility: data.roomVisibility } : {}),
      ...(data.requestEmails === false ? { requestEmails: false } : {}),
      ...(data.membersOnly ? { membersOnly: true } : {}),
      ...audiencePayload(data),
      ...createEventPlace(data, options?.listingLocation),
      ...hostPayload(data.hostProfileId),
      tags: {
        mainCategoryId: data.tags.mainCategoryId,
        subCategoryTags: data.tags.subCategoryTags,
        subSubCategoryTags: data.tags.subSubCategoryTags || [],
        amenityIds: data.tags.amenityIds || [],
      },
    }

    if (occurrences) {
      payload.occurrences = occurrences
    }

    assertValidPayload(createEventRequestSchema, payload, EVENT_FIELD_MESSAGES)

    const res = await api.api.businesses[':businessId'].events.$post({
      param: { businessId },
      json: payload,
    })
    return unwrap<{ id: string }>(res)
  },

  async update(
    businessId: string,
    eventId: string,
    data: Partial<UpdateEventInput>,
  ): Promise<void> {
    const occurrences =
      data.occurrences === undefined ? undefined : occurrenceFormsToRequests(data.occurrences)
    const hasOccurrences = occurrences && occurrences.length > 0

    const payload: UpdateEventRequest = {
      title: data.title,
      description: data.description,
      descriptionLanguage: data.descriptionLanguage,
      descriptionTranslations: data.descriptionTranslations,
      // The backend re-derives the envelope from occurrences when present; the
      // top-level range is a placeholder in that case.
      startTime: hasOccurrences
        ? occurrences[0].startTime
        : data.startTime
          ? new Date(data.startTime).toISOString()
          : undefined,
      endTime: hasOccurrences
        ? occurrences[0].endTime
        : data.endTime
          ? new Date(data.endTime).toISOString()
          : undefined,
      occurrences,
      ticketUrl: data.ticketUrl ?? undefined,
      ticketUrlKind: data.ticketUrlKind,
      capacity: data.capacity ?? undefined,
      rsvpEnabled: data.rsvpEnabled,
      liveRoomMode: data.liveRoomMode,
      roomVisibility: data.roomVisibility,
      requestEmails: data.requestEmails,
      membersOnly: data.membersOnly,
      ...audiencePayload(data),
      ...hostPayload(data.hostProfileId),
    }

    if (data.location) {
      const pending = data.locationPending ?? false
      payload.location = eventLocationRequest(
        { ...data.location, country: data.location.country || 'Austria' },
        pending,
      )
    }
    if (data.locationPending !== undefined) payload.locationPending = data.locationPending
    if (data.addressHidden !== undefined) payload.addressHidden = data.addressHidden

    if (data.price) {
      payload.price = {
        kind: data.price.kind,
        priceFixed: 'priceFixed' in data.price ? data.price.priceFixed : undefined,
        priceRangeMin: 'priceRangeMin' in data.price ? data.price.priceRangeMin : undefined,
        priceRangeMax: 'priceRangeMax' in data.price ? data.price.priceRangeMax : undefined,
      }
    }

    if (data.tags) {
      payload.tags = {
        mainCategoryId: data.tags.mainCategoryId,
        subCategoryTags: data.tags.subCategoryTags,
        subSubCategoryTags: data.tags.subSubCategoryTags || [],
        amenityIds: data.tags.amenityIds || [],
      }
    }

    assertValidPayload(updateEventRequestSchema, payload, EVENT_FIELD_MESSAGES)

    const res = await api.api.businesses[':businessId'].events[':id'].$put({
      param: { businessId, id: eventId },
      json: payload,
    })
    await unwrap<void>(res)
  },

  async delete(businessId: string, eventId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].events[':id'].$delete({
      param: { businessId, id: eventId },
    })
    await unwrap<void>(res)
  },

  async publish(businessId: string, eventId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].events[':id'].status.$post({
      param: { businessId, id: eventId },
      json: { action: 'publish' },
    })
    await unwrap<void>(res)
  },

  async cancel(businessId: string, eventId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].events[':id'].status.$post({
      param: { businessId, id: eventId },
      json: { action: 'cancel' },
    })
    await unwrap<void>(res)
  },

  async unlist(businessId: string, eventId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].events[':id'].status.$post({
      param: { businessId, id: eventId },
      json: { action: 'unlist' },
    })
    await unwrap<void>(res)
  },

  async checkinCode(businessId: string, eventId: string): Promise<EventCheckinCodeResponse> {
    const res = await api.api.businesses[':businessId'].events[':id']['checkin-code'].$post({
      param: { businessId, id: eventId },
    })
    return unwrap<EventCheckinCodeResponse>(res)
  },

  async liveRoom(businessId: string, eventId: string): Promise<EventLiveRoomStatus> {
    const res = await api.api.businesses[':businessId'].events[':id']['live-room'].$get({
      param: { businessId, id: eventId },
    })
    return unwrap<EventLiveRoomStatus>(res)
  },

  async endLiveRoom(businessId: string, eventId: string): Promise<EventLiveRoomEndResponse> {
    const res = await api.api.businesses[':businessId'].events[':id']['live-room'].end.$post({
      param: { businessId, id: eventId },
    })
    return unwrap<EventLiveRoomEndResponse>(res)
  },

  async announce(
    businessId: string,
    eventId: string,
    text: string,
  ): Promise<LiveRoomAnnouncementResponse> {
    const res = await api.api.businesses[':businessId'].events[':id'][
      'live-room'
    ].announcements.$post({ param: { businessId, id: eventId }, json: { text } })
    return unwrap<LiveRoomAnnouncementResponse>(res)
  },
}
